import { useMemo } from "react";
import { useSearchParams } from "react-router";

import { buildProductUrl } from "~/hec/utils/product-slug";
import type { C_ProductInfo } from "~/lib/app.types";
import type { SerializeFrom } from "~/lib/types";

export function useVariantUrlSync({
  product,
  suppressUrlSync,
  onVariantChange,
}: {
  product: SerializeFrom<C_ProductInfo>;
  suppressUrlSync?: boolean;
  onVariantChange?: (values: Record<string, string>) => void;
}) {
  const [params] = useSearchParams();

  const variationAttributeIds = useMemo(
    () => new Set(product.variationAttributes?.map(a => a.id) || []),
    [product.variationAttributes],
  );

  const variants = (
    product as unknown as {
      variants?: Array<{
        orderable?: boolean;
        variationValues?: Record<string, string>;
      }>;
    }
  )?.variants;

  const defaultValue = useMemo(() => {
    const base: Record<string, string> = {
      ...(product?.variationValues as Record<string, string> | undefined),
    };
    // Auto-select first orderable variant when nothing pre-set (master URL load).
    // Prefer orderable; fall back to variants[0] so the customer still sees
    // selection state and ATC renders "Out of Stock" instead of
    // "Select all product attributes".
    if (Object.keys(base).length === 0 && variants?.length) {
      const fallback = variants.find(v => v.orderable !== false) ?? variants[0];
      if (fallback?.variationValues) {
        Object.assign(base, fallback.variationValues);
      }
    }
    if (!suppressUrlSync) {
      for (const [key, value] of params.entries()) {
        if (variationAttributeIds.has(key)) {
          base[key] = value;
        }
      }
    }
    return base;
  }, [
    product?.variationValues,
    variants,
    suppressUrlSync,
    params,
    variationAttributeIds,
  ]);

  const onChange = (values: Record<string, string>) => {
    if (suppressUrlSync) {
      onVariantChange?.(values as Record<string, string>);
      return;
    }
    // Variant pips — the small clickable swatches / buttons in the PDP
    // variant selector (e.g. color circles, size chips) — mirror selected
    // state to the URL for deep-linking, bookmarks, and back/forward
    // navigation. They are NOT a route change — the loader has nothing new
    // to fetch — so we update the URL with `history.replaceState` directly
    // instead of `setSearchParams`. Going through React Router's navigation
    // pipeline would fire `shouldRevalidate` on the catch-all dispatcher
    // and re-run `runProductLoader` (Builder + SCAPI + CIO recs +
    // PowerReviews + …) on every swatch click. Bypassing the pipeline keeps
    // the URL share/refresh contract intact while letting PLP filter clicks
    // (which DO need a loader rerun) revalidate via the dispatcher's
    // `currentUrl.search !== nextUrl.search` gate.
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    Object.entries(values).forEach(([key, value]) => {
      if (!variationAttributeIds.has(key)) return;
      if (value) {
        url.searchParams.set(key, value);
      } else {
        url.searchParams.delete(key);
      }
    });
    // Upgrade bare `/{id}.html` PDP paths to the slugged
    // `/{slug}/{id}.html` form on first variant click (SFCC-11163), so
    // back-button / share URLs stay consistent with PLP tile links and
    // canonical. Slug derives from the loaded SCAPI product name. A path
    // already carrying a slug (segment count > 1) is left alone — the
    // customer arrived on a slugged URL and we don't want to mutate the
    // segment a Builder author / SEO crawler is observing. Heuristic is
    // single-locale-safe (sites are en-US only per `config/sites.ts`) and
    // would need revisiting if a locale path prefix ever lands.
    const segments = url.pathname.split("/").filter(Boolean);
    if (product.id && product.name && segments.length === 1) {
      url.pathname = buildProductUrl(product.id, product.name);
    }
    window.history.replaceState(window.history.state, "", url.toString());
  };

  return { defaultValue, onChange };
}
